/**
 * A sub's billing on a job, its card's Billing tab (Duncan 2026-10-29). Its subcontract amount -- added with the
 * executed subcontract on the Documents tab, and changed there -- and its schedule of values: the PM's own lines
 * ("completely custom"), in sections, each with a category.
 *
 * As the job's own SOV in every way (Duncan the same day: "needs to have sections on the SOV as well. its got to look
 * similar to what we have for the customer sov stuff"; then "the process needs to be very similar to the project
 * managers already existing deal ... We start with a task and then link a schedule value to it"):
 *  - laid out as the SOV tab (SOVTab.js): its tiles -- total scheduled value, allocated to tasks, unallocated, status --
 *    after the subcontract's; the Schedule of Values bar with + Section and + Line; sections as bands (a dot, the
 *    count, the subtotals); item numbers; each line's category pill; every billing column, nothing billed until the
 *    sub's pay apps come in;
 *  - lines made here with no tasks; a task takes a share of a line from the task, as SOV links -- its window on this
 *    card (its Schedule or Tasks tab: Sub SOV links, TaskSubSovLinks.js), not the Schedule's Task Properties (Duncan:
 *    "when the project managers [are] setting up the schedule ... they will not know the subcontract stuff yet").
 *    A line no task takes is unallocated, and bills nothing.
 * One window adds or changes a line (Save and Cancel), and shows the tasks linked to it.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from '../ui';
import { ErrorBanner, ModalShell, PrimaryButton, SecondaryButton, fieldStyle } from './SubUi';
import ConfirmDialog from '../ConfirmDialog';
import useConfirm from '../../hooks/useConfirm';
import {
  getSubBilling, addSubSovLine, updateSubSovLine, removeSubSovLine, addSubSovSection, renameSubSovSection, removeSubSovSection,
} from '../../api';
import { centsFromText, moneyOfCents, fmtDay } from '../../lib/subcontractors';
import { sovSectionColor, getCategoryStyle, SOV_CATEGORIES } from '../../lib/sovStyle';
import { numberLines } from '../../lib/subSov';
import { AllocationBar, AllocationDonut, slicesOf, VIZ_CSS } from './SubSovCharts';

const muted = { fontSize: 13, color: 'var(--text-3)' };
const smallBtn = { height: 32, fontSize: 13, padding: '0 14px' };
const input = { ...fieldStyle, height: 36, fontSize: 14 };
const fieldLabel = { display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', marginBottom: 5 };
const NEW_SECTION = '__new__';
const s = (n) => (n === 1 ? '' : 's');
/** A place said short, as a PM says it: "Bluebonnet ES", "Lone Star MS", "Lakeview HS". */
const shortPlace = (p) => String(p || '').replace(/\s+Elementary School$/i, ' ES').replace(/\s+Middle School$/i, ' MS').replace(/\s+High School$/i, ' HS');

// The table's columns, as the job's SOV (SOVTab's COL_NAMES), the description taking the room left.
const COLS = [
  ['Item No.', '88px', 'left'], ['Description', 'minmax(280px, 1fr)', 'left'], ['Category', '128px', 'left'],
  ['Sched. Value', '132px', 'right'], ['Previous', '108px', 'right'], ['This Period', '108px', 'right'], ['Stored Mat.', '108px', 'right'],
  ['Total', '108px', 'right'], ['%', '52px', 'center'], ['Balance', '124px', 'right'], ['Retainage', '100px', 'right'], ['Released', '96px', 'right'],
  ['', '36px', 'left'],
];
const GRID = COLS.map((c) => c[1]).join(' ');
const MIN_WIDTH = 1448;

/** The tasks linked to a line, in words: "HVAC Replacement · Bluebonnet ES", or "HVAC Replacement at 5 places". */
function linkedWords(links) {
  const byName = new Map();
  links.forEach((k) => { const n = k.task.task_name || k.task.task_code; if (!byName.has(n)) byName.set(n, []); byName.get(n).push(shortPlace(k.task.building_area_phase)); });
  const phrases = [...byName.entries()].map(([name, places]) => {
    const uniq = [...new Set(places.filter(Boolean))];
    return uniq.length > 1 ? `${name} at ${uniq.length} places` : uniq.length ? `${name} · ${uniq[0]}` : name;
  });
  return phrases.length > 2 ? `${phrases.slice(0, 2).join(' · ')} · and ${phrases.length - 2} more` : phrases.join(' · ');
}

/** A money box: shows "$9,500.00", takes "9500" or "$9,500". */
function MoneyField({ id, value, onChange }) {
  return (
    <input id={id} value={value} onChange={(e) => onChange(e.target.value)} inputMode="decimal" placeholder="$0.00"
      onBlur={() => { const c = centsFromText(value); if (c != null) onChange(moneyOfCents(c)); }}
      style={{ ...input, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} />
  );
}

/** A category's pill, as on the job's SOV. */
const CategoryPill = ({ category }) => (category ? (
  <span style={{ ...getCategoryStyle(category), padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 500, display: 'inline-block', whiteSpace: 'nowrap' }}>
    {category}
  </span>
) : <span style={{ color: 'var(--text-3)' }}>–</span>);

/**
 * Adding a line, or changing one (`line`): what it's for, its value, its section and category -- and, for one that's
 * there, the tasks linked to it (linked and unlinked from each task's window on this card). Save or Cancel; Remove for a
 * line that's there. A new section can be made from here.
 */
function LineWindow({ sub, lines, sections, line, number, contractCents, preset, onClose, onSaved, onRemove }) {
  const editing = !!line;
  const [description, setDescription] = useState(line ? line.description : '');
  const [value, setValue] = useState(line ? moneyOfCents(line.value_cents) : (preset && preset.value_cents != null ? moneyOfCents(preset.value_cents) : ''));
  const [section, setSection] = useState(String((line ? line.section_id : preset && preset.section_id) || ''));
  const [newSection, setNewSection] = useState('');
  const [category, setCategory] = useState(line ? line.category || '' : 'Labor');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [hot, setHot] = useState(null); // the donut's slice pointed at, in the ring or the list

  // What's left of the subcontract with this line's value as typed.
  const others = lines.filter((l) => !line || l.id !== line.id).reduce((n, l) => n + l.value_cents, 0);
  const typed = centsFromText(value);
  const left = contractCents - others - (typed || 0);
  const links = line ? line.links : [];
  const allocated = line ? line.allocated_cents : 0;

  const save = async () => {
    const d = description.replace(/\s+/g, ' ').trim();
    if (!d) return setError('Say what the line is for, as on the sub’s schedule of values.');
    if (typed == null) return setError('Type the line’s value.');
    if (section === NEW_SECTION && !newSection.trim()) return setError('Name the new section, or pick one.');
    setBusy(true); setError(null);
    try {
      let sectionId = section && section !== NEW_SECTION ? Number(section) : null;
      if (section === NEW_SECTION) sectionId = (await addSubSovSection(sub.projectId, sub.id, newSection.trim())).id;
      if (editing) {
        const patch = {};
        if (d !== line.description) patch.description = d;
        if (typed !== line.value_cents) patch.value_cents = typed;
        if ((sectionId || null) !== (line.section_id || null)) patch.section_id = sectionId;
        if ((category || null) !== (line.category || null)) patch.category = category || null;
        if (Object.keys(patch).length) await updateSubSovLine(sub.projectId, sub.id, line.id, patch);
      } else {
        await addSubSovLine(sub.projectId, sub.id, { description: d, value_cents: typed, section_id: sectionId, category: category || null });
      }
      onSaved();
    } catch (e) {
      setError(e.message || 'Couldn’t save the line.');
      setBusy(false);
    }
  };

  const leftText = left === 0 ? 'Every dollar of the subcontract is on a line'
    : left > 0 ? `${moneyOfCents(left)} of the subcontract is left` : `${moneyOfCents(-left)} over the subcontract`;
  return (
    <ModalShell title={editing ? `Line ${number} · ${sub.name}` : `New line · ${sub.name}`} onClose={onClose} busy={busy} width={editing ? 760 : 640}
      footer={(
        <>
          {editing && <SecondaryButton danger onClick={() => onRemove(line, number)} disabled={busy} style={{ marginRight: 'auto' }}>Remove line</SecondaryButton>}
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy} onClick={save}>{editing ? 'Save line' : 'Add line'}</PrimaryButton>
        </>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 200px', gap: 12 }}>
          <div>
            <label htmlFor="sov-line-what" style={fieldLabel}>Description</label>
            <input id="sov-line-what" value={description} onChange={(e) => setDescription(e.target.value)} autoFocus={!editing}
              placeholder="As on the sub’s schedule of values: Labor: elementary schools" style={input} />
          </div>
          <div>
            <label htmlFor="sov-line-value" style={fieldLabel}>Scheduled value</label>
            <MoneyField id="sov-line-value" value={value} onChange={setValue} />
            <div style={{ fontSize: 12, marginTop: 4, color: left < 0 ? 'var(--danger)' : 'var(--text-3)' }}>
              {leftText}
              {left > 0 && (
                <> · <button type="button" onClick={() => setValue(moneyOfCents((typed || 0) + left))}
                  style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' }}>
                  add it
                </button></>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 200px', gap: 12 }}>
          <div>
            <label htmlFor="sov-line-section" style={fieldLabel}>Section</label>
            <select id="sov-line-section" value={section} onChange={(e) => setSection(e.target.value)} style={{ ...input, cursor: 'pointer' }}>
              <option value="">— No section —</option>
              {sections.map((x) => <option key={x.id} value={String(x.id)}>{x.name}</option>)}
              <option value={NEW_SECTION}>+ Add new section…</option>
            </select>
            {section === NEW_SECTION && (
              <input value={newSection} onChange={(e) => setNewSection(e.target.value)} autoFocus placeholder="New section’s name: Equipment & Materials"
                aria-label="New section’s name" style={{ ...input, marginTop: 6 }} />
            )}
          </div>
          <div>
            <label htmlFor="sov-line-category" style={fieldLabel}>Category</label>
            <select id="sov-line-category" value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...input, cursor: 'pointer' }}>
              <option value="">— None —</option>
              {SOV_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        {editing && (
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
              <span style={{ fontSize: 14, fontWeight: 650, color: 'var(--text-1)' }}>Linked tasks</span>
              <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>{moneyOfCents(allocated)} allocated of {moneyOfCents(line.value_cents)}</span>
            </div>
            {/* Where it's allocated (Duncan 2026-10-29: "Like pie graphs"): a donut beside the tasks, their chips its colors. */}
            {(() => {
              const { slices, colorOf } = slicesOf(links);
              const sliceOf = (k) => (slices.find((x) => x.links.some((y) => y.id === k.id)) || {}).key;
              const notLinked = line.value_cents - allocated;
              return (
                <div className="sov-viz" style={{ display: 'grid', gridTemplateColumns: '156px minmax(0, 1fr)', gap: 16, alignItems: 'center' }}>
                  <AllocationDonut links={links} valueCents={line.value_cents} hot={hot} onHot={setHot} />
                  <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, overflow: 'hidden', alignSelf: 'center' }}>
                    {links.map((k, i) => {
                      const key = sliceOf(k);
                      return (
                        <div key={k.id} onMouseEnter={() => setHot(key)} onMouseLeave={() => setHot(null)}
                          style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 12px', borderTop: i ? '1px solid var(--border-subtle)' : 'none', fontSize: 13.5,
                            background: hot && hot === key ? 'var(--bg-surface-2)' : 'transparent', transition: 'background .12s' }}>
                          <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: colorOf.get(k.id), flex: 'none', alignSelf: 'center' }} />
                          <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>{k.task.task_code}</span>
                          <span style={{ flex: 1, minWidth: 0, color: 'var(--text-1)' }}>{k.task.task_name}<span style={{ color: 'var(--text-3)' }}> · {shortPlace(k.task.building_area_phase)}</span></span>
                          <span className="mono" style={{ color: 'var(--text-1)', whiteSpace: 'nowrap' }}>
                            {k.allocation_method === 'Percentage' && k.allocation_percent != null ? `${Math.round(k.allocation_percent * 100) / 100}% · ` : ''}{moneyOfCents(k.allocation_amount_cents)}
                          </span>
                        </div>
                      );
                    })}
                    {notLinked > 0 && (
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, padding: '8px 12px', borderTop: links.length ? '1px solid var(--border-subtle)' : 'none', fontSize: 13.5 }}>
                        <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--sv-track)', border: '1px solid var(--border-strong)', flex: 'none', alignSelf: 'center', boxSizing: 'border-box' }} />
                        <span style={{ flex: 1, minWidth: 0, color: 'var(--text-3)' }}>{links.length ? 'Not linked to a task yet' : 'No task is linked to this line yet'}</span>
                        <span className="mono" style={{ color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{moneyOfCents(notLinked)}</span>
                      </div>
                    )}
                    {notLinked < 0 && (
                      <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--danger)' }}>
                        The tasks take {moneyOfCents(-notLinked)} more than the line’s value. Raise the value, or change a task’s link.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
            <div style={{ ...muted, fontSize: 12.5, marginTop: 6 }}>Link a task to it from the task: this card’s Schedule or Tasks tab → click the task → Sub SOV links.</div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

/** + Section, or a section's pencil: its name; Remove for one that's there (its lines stay, with no section). */
function SectionWindow({ sub, section, lineCount, onClose, onSaved, onRemove }) {
  const [name, setName] = useState(section ? section.name : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const save = async () => {
    const n = name.replace(/\s+/g, ' ').trim();
    if (!n) return setError('Name the section.');
    setBusy(true); setError(null);
    try {
      if (section) { if (n !== section.name) await renameSubSovSection(sub.projectId, sub.id, section.id, n); } else await addSubSovSection(sub.projectId, sub.id, n);
      onSaved();
    } catch (e) {
      setError(e.message || 'Couldn’t save the section.');
      setBusy(false);
    }
  };
  return (
    <ModalShell title={section ? `Section · ${sub.name}` : `New section · ${sub.name}`} onClose={onClose} busy={busy} width={480}
      subtitle={section ? `${lineCount} line${s(lineCount)} in it` : 'Group lines on the schedule of values, as on the job’s own SOV.'}
      footer={(
        <>
          {section && <SecondaryButton danger onClick={() => onRemove(section, lineCount)} disabled={busy} style={{ marginRight: 'auto' }}>Remove section</SecondaryButton>}
          <SecondaryButton onClick={() => !busy && onClose()}>Cancel</SecondaryButton>
          <PrimaryButton busy={busy} onClick={save}>{section ? 'Save' : 'Add section'}</PrimaryButton>
        </>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div>
          <label htmlFor="sov-section-name" style={fieldLabel}>Section name</label>
          <input id="sov-section-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') save(); }} placeholder="Equipment & Materials" style={input} />
        </div>
      </div>
    </ModalShell>
  );
}

export default function SubBilling({ projectId, sub, onOpenDocuments }) {
  const [data, setData] = useState(null); // { contract, sections, lines }
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // { line, number } or { preset } (a new one)
  const [sectionEdit, setSectionEdit] = useState(null); // { section } (null section: a new one)
  const { confirm, confirmDialogProps } = useConfirm();
  const load = useCallback(() => getSubBilling(projectId, sub.id)
    .then((d) => { setData(d); setError(null); })
    .catch((e) => setError(e.message || 'Couldn’t load the billing')), [projectId, sub.id]);
  useEffect(() => { load(); }, [load]);

  const paper = (sub.documents || []).find((d) => d.doc_type === 'subcontract') || null;
  const who = { ...sub, projectId };

  if (!data && !error) return <div style={muted}>Loading…</div>;
  if (!data) return <ErrorBanner>{error}</ErrorBanner>;
  const { contract } = data;
  const sections = data.sections || [];

  if (!contract) {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 18px', borderRadius: 12, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>
        <span style={{ color: 'var(--text-3)', marginTop: 2 }}><Icon name="template" size={18} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 650, color: 'var(--text-1)' }}>No subcontract for this job yet</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-2)', marginTop: 3, lineHeight: 1.5 }}>
            First, add the executed subcontract on the Documents tab with its amount. {sub.name}’s schedule of values is split from that amount.
          </div>
          <PrimaryButton onClick={onOpenDocuments} style={{ ...smallBtn, marginTop: 12 }}>Add it on Documents</PrimaryButton>
        </div>
      </div>
    );
  }

  const { groups, lines } = numberLines(data.lines, sections);
  const amount = contract.amount_cents;
  const total = lines.reduce((t, l) => t + l.value_cents, 0);
  const allocated = lines.reduce((t, l) => t + l.allocated_cents, 0);
  const unallocated = total - allocated;
  const offContract = amount - total;

  const remove = async (line, number) => {
    const n = line.links.length;
    const ok = await confirm({
      title: `Remove line ${number}?`,
      message: `“${line.description}”, ${moneyOfCents(line.value_cents)}, comes off ${sub.name}’s schedule of values${n ? `, and its ${n} task link${s(n)} with it` : ''}.`,
      confirmLabel: 'Remove line',
      danger: true,
    });
    if (!ok) return;
    try {
      await removeSubSovLine(projectId, sub.id, line.id);
      setEditing(null);
      await load();
    } catch (e) {
      setError(e.message || 'Couldn’t remove the line');
    }
  };
  const removeSection = async (section, count) => {
    const ok = await confirm({
      title: `Remove the section “${section.name}”?`,
      message: count ? `Its ${count} line${s(count)} stay on the schedule of values, with no section.` : 'It has no lines.',
      confirmLabel: 'Remove section',
      danger: true,
    });
    if (!ok) return;
    try {
      await removeSubSovSection(projectId, sub.id, section.id);
      setSectionEdit(null);
      await load();
    } catch (e) {
      setError(e.message || 'Couldn’t remove the section');
    }
  };
  const saved = async () => { setEditing(null); setSectionEdit(null); await load(); };

  // The tiles, as the job's SOV tab's (SOVTab.js).
  const card = { flex: 1, minWidth: 190, padding: '14px 16px', borderRadius: 10, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', boxShadow: 'var(--shadow-sm)' };
  const tileLabel = { fontSize: 11.5, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-3)', marginBottom: 6 };
  const tileValue = { fontSize: 23.5, fontWeight: 500, color: 'var(--text-1)', letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums' };
  const tileSub = { fontSize: 12.5, color: 'var(--text-3)', marginTop: 2 };
  const isOver = allocated > total;
  const isBalanced = total > 0 && allocated === total;
  const tone = isBalanced ? { fg: 'var(--success)', bg: 'var(--success-soft)', text: 'Balanced' }
    : isOver ? { fg: 'var(--danger)', bg: 'var(--danger-soft)', text: 'Over-allocated' } : { fg: 'var(--warning)', bg: 'var(--warning-soft)', text: 'Under-allocated' };
  const linkBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit' };
  const cell = (align) => ({ padding: '0 10px', textAlign: align, minWidth: 0 });
  const num = { fontSize: 14, fontWeight: 500, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' };

  const lineRow = (l, last) => {
    const open = () => setEditing({ line: l, number: l.number });
    const loose = l.value_cents - l.allocated_cents;
    return (
      <div key={l.id} role="button" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
        className="sov-line" title="Change this line"
        style={{ display: 'grid', gridTemplateColumns: GRID, padding: '14px 0', alignItems: 'center', cursor: 'pointer', minWidth: MIN_WIDTH,
          borderBottom: last ? 'none' : '1px solid var(--border-subtle)' }}>
        <div className="mono" style={{ ...cell('left'), fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', letterSpacing: '-0.02em' }}>{l.number}</div>
        <div style={cell('left')}>
          <div style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--text-1)', letterSpacing: '-0.005em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.description}</div>
          <AllocationBar links={l.links} valueCents={l.value_cents} />
          {l.links.length ? (
            <div style={{ fontSize: 12.5, color: loose > 0 ? 'var(--warning)' : 'var(--text-3)', marginTop: 1, lineHeight: 1.4 }}>
              {l.links.length} task{s(l.links.length)}: {linkedWords(l.links)}{loose > 0 ? ` · ${moneyOfCents(loose)} not linked yet` : loose < 0 ? ` · over by ${moneyOfCents(-loose)}` : ''}
            </div>
          ) : (
            <div style={{ fontSize: 12.5, color: 'var(--warning)', marginTop: 1 }}>Not linked to a task yet</div>
          )}
        </div>
        <div style={cell('left')}><CategoryPill category={l.category} /></div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-1)' }}>{moneyOfCents(l.value_cents)}</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-3)' }}>{moneyOfCents(0)}</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-1)' }}>{moneyOfCents(0)}</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-3)' }}>{moneyOfCents(0)}</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-1)' }}>{moneyOfCents(0)}</div>
        <div className="mono" style={{ ...cell('center'), fontSize: 13, color: 'var(--text-3)' }}>0%</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-1)' }}>{moneyOfCents(l.value_cents)}</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-3)' }}>{moneyOfCents(0)}</div>
        <div className="mono" style={{ ...cell('right'), ...num, color: 'var(--text-3)' }}>–</div>
        <div style={{ ...cell('center'), color: 'var(--text-3)', display: 'inline-flex', justifyContent: 'center' }}><Icon name="pencil" size={14} /></div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {/* The subcontract, then the job SOV's four tiles */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <div style={card}>
          <div style={tileLabel}>Subcontract</div>
          <div className="mono" style={tileValue}>{moneyOfCents(amount)}</div>
          <div style={tileSub}>
            {[paper && paper.signed_date && `Signed ${fmtDay(paper.signed_date)}`, paper && paper.number && `CE #${paper.number}`, !paper && 'Its PDF was removed'].filter(Boolean).join(' · ')}
            {(paper && (paper.signed_date || paper.number)) || !paper ? ' · ' : ''}
            <button type="button" onClick={onOpenDocuments} style={linkBtn}>Change on Documents</button>
          </div>
        </div>
        <div style={card}>
          <div style={tileLabel}>Total Scheduled Value</div>
          <div className="mono" style={{ ...tileValue, color: offContract === 0 && lines.length ? 'var(--success)' : 'var(--text-1)' }}>{moneyOfCents(total)}</div>
          <div style={tileSub}>
            {lines.length} SOV line{s(lines.length)}
            {offContract === 0 && lines.length
              ? <span style={{ marginLeft: 8, color: 'var(--success)', fontWeight: 500 }}>&#10003; Matches the subcontract</span>
              : <span className="mono" style={{ marginLeft: 8, color: 'var(--warning)', fontWeight: 500 }}>{offContract > 0 ? 'Under' : 'Over'} by {moneyOfCents(Math.abs(offContract))}</span>}
          </div>
        </div>
        <div style={card}>
          <div style={tileLabel}>Allocated to Tasks</div>
          <div className="mono" style={{ ...tileValue, color: isBalanced ? 'var(--success)' : isOver ? 'var(--danger)' : 'var(--text-1)' }}>{moneyOfCents(allocated)}</div>
          <div className="mono" style={tileSub}>{total > 0 ? ((allocated / total) * 100).toFixed(1) : '0.0'}% of the sub’s SOV</div>
        </div>
        <div style={card}>
          <div style={tileLabel}>{isOver ? 'Over-Allocated' : 'Unallocated'}</div>
          <div className="mono" style={{ ...tileValue, color: isOver ? 'var(--danger)' : unallocated > 0 ? 'var(--warning)' : 'var(--success)' }}>{moneyOfCents(Math.abs(unallocated))}</div>
          <div style={tileSub}>{isBalanced ? 'Fully allocated' : isOver ? 'exceeds the SOV' : 'remaining to link'}</div>
        </div>
        <div style={{ ...card, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <div style={tileLabel}>Status</div>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 999, fontSize: 13, fontWeight: 500, marginTop: 4,
            background: tone.bg, color: tone.fg, border: `1px solid color-mix(in oklch, ${tone.fg} 22%, transparent)`, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
            <span style={{ width: 5, height: 5, borderRadius: 99, background: tone.fg, flexShrink: 0 }} />
            {tone.text}
          </div>
        </div>
      </div>

      {/* The bar, as the job's SOV: title and count, + Section, + Line */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>Schedule of Values</span>
            <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)' }}>{lines.length} line{s(lines.length)}</span>
          </div>
          <span style={{ flex: 1 }} />
          <SecondaryButton onClick={() => setSectionEdit({ section: null })} style={{ ...smallBtn, height: 30 }}><Icon name="plus" size={13} />Section</SecondaryButton>
          <PrimaryButton onClick={() => setEditing({ preset: { value_cents: offContract > 0 ? offContract : null } })} style={{ ...smallBtn, height: 30 }}><Icon name="plus" size={13} />Line</PrimaryButton>
        </div>

        {lines.length === 0 && !sections.length ? (
          <div style={{ padding: '28px 20px', borderRadius: 12, border: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 650, color: 'var(--text-1)' }}>No SOV lines yet</div>
            <div style={{ fontSize: 13.5, color: 'var(--text-2)', margin: '6px auto 14px', lineHeight: 1.55, maxWidth: 560 }}>
              Add {sub.name}’s schedule of values lines, as on the job’s own SOV, splitting the {moneyOfCents(amount)} subcontract. Then link them
              from each task, as you do today: this card’s Schedule or Tasks tab → click the task → Sub SOV links.
            </div>
            <div style={{ display: 'inline-flex', gap: 8 }}>
              <SecondaryButton onClick={() => setSectionEdit({ section: null })} style={smallBtn}><Icon name="plus" size={13} />Section</SecondaryButton>
              <PrimaryButton onClick={() => setEditing({ preset: { value_cents: amount } })} style={smallBtn}><Icon name="plus" size={13} />Add a line</PrimaryButton>
            </div>
          </div>
        ) : (
          <>
            <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 12, background: 'var(--bg-surface)', overflowX: 'auto', boxShadow: 'var(--shadow-sm)' }}>
              <div style={{ display: 'grid', gridTemplateColumns: GRID, padding: '12px 0', background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-subtle)',
                fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-3)', minWidth: MIN_WIDTH }}>
                {COLS.map(([name, , align]) => <div key={name || 'edit'} style={{ ...cell(align), overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{name}</div>)}
              </div>
              {groups.map((g, gi) => {
                const scheduled = g.lines.reduce((t, l) => t + l.value_cents, 0);
                const name = g.section ? g.section.name : '(Ungrouped)';
                const showBand = sections.length > 0;
                return (
                  <React.Fragment key={g.section ? g.section.id : 'loose'}>
                    {showBand && (
                      <div style={{ display: 'grid', gridTemplateColumns: GRID, padding: '10px 0', background: 'var(--bg-surface-2)', alignItems: 'center', minWidth: MIN_WIDTH,
                        borderTop: gi ? '1px solid var(--border-subtle)' : 'none', borderBottom: '1px solid var(--border-subtle)' }}>
                        <div style={cell('left')} />
                        <div style={{ gridColumn: 'span 3', padding: '0 10px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: 999, background: sovSectionColor(name), flexShrink: 0 }} />
                          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                          <span className="mono" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-3)', flexShrink: 0 }}>{g.lines.length}</span>
                          {g.section && (
                            <button type="button" onClick={() => setEditing({ preset: { section_id: g.section.id, value_cents: offContract > 0 ? offContract : null } })} style={{ ...linkBtn, marginLeft: 6 }}>
                              + Add a line here
                            </button>
                          )}
                        </div>
                        <div className="mono" style={{ gridColumn: 'span 8', padding: '0 10px', fontSize: 12.5, color: 'var(--text-3)', display: 'flex', justifyContent: 'flex-end', gap: 14, fontVariantNumeric: 'tabular-nums' }}>
                          <span><span style={{ marginRight: 4 }}>Scheduled</span><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{moneyOfCents(scheduled)}</span></span>
                          <span><span style={{ marginRight: 4 }}>Billed</span><span style={{ color: 'var(--text-1)', fontWeight: 500 }}>{moneyOfCents(0)}</span></span>
                        </div>
                        <div style={{ ...cell('center'), display: 'inline-flex', justifyContent: 'center' }}>
                          {g.section && (
                            <button type="button" onClick={() => setSectionEdit({ section: g.section })} aria-label={`Rename or remove the section ${name}`} title="Rename or remove the section"
                              style={{ border: 'none', background: 'transparent', padding: 2, cursor: 'pointer', color: 'var(--text-3)', display: 'inline-flex' }}>
                              <Icon name="pencil" size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                    {g.lines.map((l, i) => lineRow(l, i === g.lines.length - 1 && gi === groups.length - 1))}
                    {showBand && g.section && !g.lines.length && (
                      <div style={{ padding: '12px 20px', fontSize: 12.5, color: 'var(--text-3)', minWidth: MIN_WIDTH, borderBottom: gi === groups.length - 1 ? 'none' : '1px solid var(--border-subtle)' }}>
                        No lines in this section yet.
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              {/* The total, as the job's SOV */}
              <div style={{ display: 'grid', gridTemplateColumns: GRID, padding: '12px 0', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)',
                fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)', alignItems: 'center', minWidth: MIN_WIDTH, letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums' }}>
                <div style={cell('left')} />
                <div style={cell('left')}>
                  <span style={{ fontSize: 11.5, fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-3)', marginRight: 6 }}>Total</span>
                  <span className="mono" style={{ color: 'var(--text-2)', fontSize: 12.5 }}>{lines.length} line{s(lines.length)}</span>
                </div>
                <div style={cell('left')} />
                <div className="mono" style={cell('right')}>{moneyOfCents(total)}</div>
                <div className="mono" style={cell('right')}>{moneyOfCents(0)}</div>
                <div className="mono" style={cell('right')}>{moneyOfCents(0)}</div>
                <div className="mono" style={cell('right')}>{moneyOfCents(0)}</div>
                <div className="mono" style={cell('right')}>{moneyOfCents(0)}</div>
                <div className="mono" style={cell('center')}>0%</div>
                <div className="mono" style={cell('right')}>{moneyOfCents(total)}</div>
                <div className="mono" style={{ ...cell('right'), color: 'var(--warning)' }}>{moneyOfCents(0)}</div>
                <div className="mono" style={cell('right')}>–</div>
                <div style={cell('left')} />
              </div>
            </div>
            <div style={{ ...muted, fontSize: 12.5, marginTop: 8 }}>
              Click a line to change it. Link tasks to a line from the task: this card’s Schedule or Tasks tab → click the task → Sub SOV links.
              Previous, This Period and the rest fill in as the sub’s pay apps come in.
            </div>
          </>
        )}
      </div>

      {editing && (
        <LineWindow sub={who} lines={lines} sections={sections} line={editing.line || null} number={editing.number} contractCents={amount}
          preset={editing.preset} onClose={() => setEditing(null)} onSaved={saved} onRemove={remove} />
      )}
      {sectionEdit && (
        <SectionWindow sub={who} section={sectionEdit.section} lineCount={sectionEdit.section ? lines.filter((l) => l.section_id === sectionEdit.section.id).length : 0}
          onClose={() => setSectionEdit(null)} onSaved={saved} onRemove={removeSection} />
      )}
      <style>{`.sov-line:hover { background: var(--bg-hover, var(--bg-surface-2)); } .sov-line:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }${VIZ_CSS}`}</style>
      <ConfirmDialog {...confirmDialogProps} />
    </div>
  );
}
